import { For, Show, useContext } from 'solid-js';
import { AppContext } from '../../app-context-provider/app-context-provider';
import { StructuredData, buildFullUrl, getImageUrl } from './structured-data';

/**
 * Generic item data for ItemList schema.
 * Each listing component maps its data to this format.
 */
export type ItemListItem = {
    url: string;
    name: string;
    description?: string;
    image?: string | { url?: string; sizes?: { large?: { url?: string } } };
    datePublished?: string;
    /**
     * Optional type hint for richer item markup.
     * When provided, the item object includes @type for better SEO.
     */
    itemType?: 'BlogPosting' | 'Product' | 'MedicalDevice' | 'Event' | 'VideoObject' | 'Article';
    /**
     * Optional: Event-specific location field
     */
    eventLocation?: string;
};

/** Maximum number of items to include in structured data to keep payload size reasonable */
const DEFAULT_MAX_ITEMS = 20;

/**
 * Helper to get event location name.
 * Uses the same logic as single event schema - defaults to "Online" if empty or contains "webinar"
 */
const getEventLocationName = (eventLocation?: string): string => {
    const location = eventLocation?.trim();
    // Default to "Online" if empty or contains webinar keywords
    if (!location || location.toLowerCase().includes('webinar')) {
        return 'Online';
    }
    return location;
};

type ItemListSchemaProps = {
    /** The list of items to include in the schema */
    items: ItemListItem[];
    /**
     * Optional: The type of list for itemListOrder.
     * - 'ItemListOrderDescending' for newest first
     * - 'ItemListOrderAscending' for oldest first
     * - 'ItemListUnordered' for no specific order (default)
     */
    listOrder?: 'ItemListOrderDescending' | 'ItemListOrderAscending' | 'ItemListUnordered';
    /**
     * Optional: Name of the list (e.g., "Products", "Blog Posts")
     */
    name?: string;
    /**
     * Optional: Description of the list
     */
    description?: string;
    /**
     * Optional: Maximum number of items to include in the schema.
     * Defaults to 20. Set to limit structured data payload size.
     */
    maxItems?: number;
};

/**
 * ItemList schema for listing/archive pages.
 * Helps search engines understand collections of items and can enable
 * carousel-style rich results in Google Search.
 *
 * @see https://schema.org/ItemList
 * @see https://developers.google.com/search/docs/appearance/structured-data/carousel
 */
export const ItemListSchema = (props: ItemListSchemaProps) => {
    const { siteInfo } = useContext(AppContext);

    const buildItemUrl = (itemUrl: string): string => {
        if (itemUrl.startsWith('http')) {
            return itemUrl;
        }
        return buildFullUrl('https', siteInfo.currentDomain, itemUrl);
    };

    const itemListSchema = () => {
        const allItems = props.items || [];

        if (allItems.length === 0) {
            return null;
        }

        // Limit items to maxItems (default 20) to keep structured data payload reasonable
        const maxItems = props.maxItems ?? DEFAULT_MAX_ITEMS;
        const items = allItems.slice(0, maxItems);

        const itemListElement = items.map((item, index) => {
            const listItem: Record<string, unknown> = {
                '@type': 'ListItem',
                position: index + 1,
            };

            // Use either 'url' (simple) OR 'item' (rich) - they are mutually exclusive per Google
            // We use 'item' when we have meaningful data, otherwise just 'url'
            if (item.name && item.itemType) {
                const itemObject: Record<string, unknown> = {
                    '@type': item.itemType,
                    name: item.name,
                    url: buildItemUrl(item.url),
                };

                if (item.description) {
                    itemObject.description = item.description;
                }

                if (item.image) {
                    const imageUrl = getImageUrl(item.image as any);
                    if (imageUrl) {
                        // Ensure image URL is absolute
                        itemObject.image = imageUrl.startsWith('http')
                            ? imageUrl
                            : buildItemUrl(imageUrl);
                    }
                }

                if (item.datePublished) {
                    // Use startDate for Event type, datePublished for others (BlogPosting, Article, etc.)
                    if (item.itemType === 'Event') {
                        itemObject.startDate = item.datePublished;
                    } else {
                        itemObject.datePublished = item.datePublished;
                    }
                }

                // Add location for Event items (always required for Event type)
                // Uses the same logic as single event schema
                if (item.itemType === 'Event') {
                    itemObject.location = {
                        '@type': 'Place',
                        name: getEventLocationName(item.eventLocation),
                    };
                }

                listItem.item = itemObject;
            } else {
                // Simple list item with just URL
                listItem.url = buildItemUrl(item.url);
            }

            return listItem;
        });

        const schema: Record<string, unknown> = {
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement,
        };

        if (props.name) {
            schema.name = props.name;
        }

        if (props.description) {
            schema.description = props.description;
        }

        if (props.listOrder) {
            schema.itemListOrder = `https://schema.org/${props.listOrder}`;
        }

        // Use total count, not limited count, so search engines know full list size
        schema.numberOfItems = allItems.length;

        return schema;
    };

    return (
        <Show when={props.items?.length > 0}>
            <StructuredData data={itemListSchema()!} />
        </Show>
    );
};

/**
 * Helper to map blog/news listing data to ItemListItem format
 */
export const mapBlogNewsToItemList = (posts: Array<{
    url?: string;
    permalink?: string;
    listCardContent?: { title?: string; cardText?: string; image?: { url?: string } };
    date?: string;
}>): ItemListItem[] => {
    return posts
        .filter(post => post.url || post.permalink)
        .map(post => ({
            url: post.url || post.permalink || '',
            name: post.listCardContent?.title || '',
            description: post.listCardContent?.cardText,
            image: post.listCardContent?.image?.url,
            datePublished: post.date,
            itemType: 'BlogPosting' as const,
        }));
};

/**
 * Helper to map product listing data to ItemListItem format.
 * Uses 'MedicalDevice' type since these are informational medical product pages,
 * not e-commerce products (which would require offers/pricing for Product type).
 */
export const mapProductsToItemList = (products: Array<{
    url?: string;
    permalink?: string;
    post_title?: string;
    shortDescription?: string;
    images?: Array<{ file?: { url?: string }; isFeatured?: boolean }>;
}>): ItemListItem[] => {
    return products
        .filter(product => product.url || product.permalink)
        .map(product => {
            const featuredImage = product.images?.find(img => img.isFeatured)?.file?.url
                || product.images?.[0]?.file?.url;

            return {
                url: product.url || product.permalink || '',
                name: product.post_title || '',
                description: product.shortDescription,
                image: featuredImage,
                itemType: 'MedicalDevice' as const,
            };
        });
};

/**
 * Helper to map event listing data to ItemListItem format
 */
export const mapEventsToItemList = (events: Array<{
    url?: string;
    permalink?: string;
    listCardContent?: { title?: string; cardText?: string; image?: { url?: string } };
    eventDate?: string;
    eventLocation?: string;
}>): ItemListItem[] => {
    return events
        .filter(event => event.url || event.permalink)
        .map(event => ({
            url: event.url || event.permalink || '',
            name: event.listCardContent?.title || '',
            description: event.listCardContent?.cardText,
            image: event.listCardContent?.image?.url,
            datePublished: event.eventDate,
            eventLocation: event.eventLocation,
            itemType: 'Event' as const,
        }));
};

/**
 * Helper to map video listing data to ItemListItem format
 */
export const mapVideosToItemList = (videos: Array<{
    url?: string;
    permalink?: string;
    video_title?: string;
    post_title?: string;
    image?: { url?: string } | string;
    seo?: { og_image?: any };
    date?: string;
}>): ItemListItem[] => {
    return videos
        .filter(video => video.url || video.permalink)
        .map(video => {
            let imageUrl: string | undefined;
            if (video.image) {
                if (typeof video.image === 'string') {
                    imageUrl = video.image;
                } else if (video.image.url) {
                    imageUrl = video.image.url;
                }
            }

            return {
                url: video.url || video.permalink || '',
                name: video.video_title || video.post_title || '',
                image: imageUrl,
                datePublished: video.date,
                itemType: 'VideoObject' as const,
            };
        });
};
